// NesjsCore — wraps the vendored angelo-wf/NesJs core to satisfy the
// EmulatorCore contract for the browser tier. See ../PATCHES.md for
// the (small) edits applied during vendoring.
//
// Everything shared with the headless adapter (./headless.ts) lives in
// ./core-base.ts; this file adds only the browser-tier extras: the
// background palette/tilemap introspection the devtools panel reads.
import { NesjsCoreBase } from "./core-base.js";
export const id = "nesjs";
export const label = "NesJs";
export class NesjsCore extends NesjsCoreBase {
    constructor(opts) {
        super({
            emulateSound: opts.emulateSound,
            sampleRate: opts.sampleRate,
            // The browser tier always has a display consuming pixels.
            renderPixels: true,
        });
    }
    // Reconstruct background palette: 16 entries shaped like sprite
    // palette, but reading palRam[0x00..0x0F]. Pattern-color-0 always
    // hits the universal bg color (palRam[0]) during rendering, so the
    // "color 0 of sub-palette N" slots (0x04/0x08/0x0C) alias to 0x00.
    readBackgroundPalette() {
        const ppu = this.nes.ppu;
        const palRam = ppu.paletteRam;
        const nesPal = ppu.nesPal;
        const out = new Uint32Array(16);
        for (let i = 0; i < 16; i++) {
            const palAdr = (i & 0x3) === 0 ? 0 : i;
            const colorIdx = (palRam[palAdr] ?? 0) & 0x3f;
            const entry = nesPal[colorIdx] ?? [0, 0, 0];
            const r = entry[0] & 0xff;
            const g = entry[1] & 0xff;
            const b = entry[2] & 0xff;
            out[i] = ((b << 16) | (g << 8) | r) >>> 0;
        }
        return out;
    }
    readBackgroundPatternTableBase() {
        return this.nes.ppu.bgPatternBase === 0x1000 ? 1 : 0;
    }
    // Read all four nametables ($2000-$2FFF) and unpack the attribute
    // tables into per-tile sub-palette indices. The mapper's ppuPeak
    // honors mirroring, so mirrored nametables come back with identical
    // bytes — that's fine; the picker just unions the observed pairs.
    readBackgroundTilemap() {
        const mapper = this.nes.mapper;
        const tiles = new Uint8Array(4 * 960);
        const palettes = new Uint8Array(4 * 960);
        if (!mapper)
            return { tiles, palettes };
        for (let nt = 0; nt < 4; nt++) {
            const ntBase = 0x2000 + nt * 0x400;
            const atBase = ntBase + 0x3c0;
            for (let row = 0; row < 30; row++) {
                for (let col = 0; col < 32; col++) {
                    const idx = nt * 960 + row * 32 + col;
                    tiles[idx] = mapper.ppuPeak(ntBase + row * 32 + col) & 0xff;
                    // Each attribute byte covers a 4x4-tile (32x32 px) region.
                    // Within that region: 2-bit field per 2x2-tile quadrant,
                    // packed as TR<<2 | TL — pick the right two bits by which
                    // quadrant the (col,row) lands in.
                    const attrByte = mapper.ppuPeak(atBase + (row >> 2) * 8 + (col >> 2)) & 0xff;
                    const quad = (((row & 0x02) >> 1) << 1) | ((col & 0x02) >> 1);
                    palettes[idx] = (attrByte >> (quad * 2)) & 0x03;
                }
            }
        }
        return { tiles, palettes };
    }
}
export function create(opts) {
    return new NesjsCore(opts);
}
//# sourceMappingURL=web.js.map